const CDN_MANIFEST_URL = 'https://tv-ota.myshareview.com/shareview-health-tv-ota/manifest.json';
const LOADER_VERSION = '1.0.0';
const MANIFEST_TIMEOUT_MS = 4000;
const TV_BUNDLE_URL = 'tv-bundle/index.html';
const ROOT_ELEMENT_ID = 'root';
const APP_READY_EVENT = 'svh:app-ready';
const STORAGE_KEY = 'svh-tv-loader-state';
declare const __SVH_DISABLE_OTA__: boolean;

interface LoaderManifest {
  version: string;
  minLoaderVersion?: string;
  scripts: string[];
  styles?: string[];
  publishedAt?: string;
}

interface LoaderState {
  version: string;
  manifest: Pick<LoaderManifest, 'scripts' | 'styles'>;
  lastOk: number;
}

let splashHidden = false;
const injectedAssets: HTMLElement[] = [];

function withCacheBust(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('t', Date.now().toString());
    return parsed.toString();
  } catch (error) {
    console.warn('Unable to append cache bust parameter to manifest URL', error);
    const delimiter = url.includes('?') ? '&' : '?';
    return `${url}${delimiter}t=${Date.now()}`;
  }
}

function ensureRootElement(): HTMLElement {
  const root = document.getElementById(ROOT_ELEMENT_ID) as HTMLElement | null;

  if (!root) {
    throw new Error('Loader root element is missing');
  }

  return root;
}

function hideSplash(): void {
  if (splashHidden) {
    return;
  }

  splashHidden = true;

  const boot = document.getElementById('boot') as HTMLElement | null;

  if (boot) {
    boot.setAttribute('hidden', 'true');
    try {
      boot.style.display = 'none';
    } catch (error) {
      console.warn('Unable to hide boot container via style', error);
    }
  }

  const root = document.getElementById(ROOT_ELEMENT_ID) as HTMLElement | null;

  if (root) {
    root.classList.add('ready');
  }
}

function registerReadySignal(): void {
  window.addEventListener(APP_READY_EVENT, hideSplash, { once: true });
}

function removeInjectedAssets(): void {
  while (injectedAssets.length > 0) {
    const asset = injectedAssets.pop();

    if (asset && asset.parentNode) {
      asset.parentNode.removeChild(asset);
    }
  }
}

function trackAsset<T extends HTMLElement>(node: T): T {
  injectedAssets.push(node);
  return node;
}

function injectStyle(href: string): Promise<void> {
  return new Promise(function (resolve, reject) {
    const link = trackAsset(document.createElement('link'));
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = function () {
      resolve();
    };
    link.onerror = function () {
      reject(new Error('style load failed: ' + href));
    };
    document.head.appendChild(link);
  });
}

function injectScript(src: string): Promise<void> {
  return new Promise(function (resolve, reject) {
    const script = trackAsset(document.createElement('script'));
    script.src = src;
    script.defer = true;
    script.onload = function () {
      resolve();
    };
    script.onerror = function () {
      reject(new Error('script load failed: ' + src));
    };
    document.head.appendChild(script);
  });
}

async function bootRemote(manifest: LoaderManifest): Promise<void> {
  ensureRootElement();
  removeInjectedAssets();
  registerReadySignal();

  const styles = manifest.styles || [];

  for (const href of styles) {
    await injectStyle(href);
  }

  for (const src of manifest.scripts) {
    await injectScript(src);
  }

  window.setTimeout(function () {
    hideSplash();
  }, 0);
}

function updateStatus(message: string): void {
  const statusEl = document.getElementById('loaderStatus');

  if (statusEl) {
    statusEl.textContent = message;
  }
}

function getCachedState(): LoaderState | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return null;
    }

    return JSON.parse(raw) as LoaderState;
  } catch (error) {
    console.warn('Unable to read cached loader state', error);

    return null;
  }
}

function storeState(state: LoaderState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('Unable to persist loader state', error);
  }
}

function clearState(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Unable to clear loader state', error);
  }
}

function compareVersions(currentVersion: string, requiredVersion: string): number {
  const currentParts = currentVersion.split('.');
  const requiredParts = requiredVersion.split('.');
  const maxLength = Math.max(currentParts.length, requiredParts.length);

  for (let index = 0; index < maxLength; index += 1) {
    const currentPart = parseInt(currentParts[index] || '0', 10);
    const requiredPart = parseInt(requiredParts[index] || '0', 10);

    if (currentPart > requiredPart) {
      return 1;
    }

    if (currentPart < requiredPart) {
      return -1;
    }
  }

  return 0;
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> {
  return new Promise<T>(function (resolve, reject) {
    const timeoutId = window.setTimeout(function () {
      reject(new Error(timeoutMessage));
    }, timeoutMs);

    promise.then(
      function (value) {
        window.clearTimeout(timeoutId);
        resolve(value);
      },
      function (error) {
        window.clearTimeout(timeoutId);
        reject(error);
      }
    );
  });
}

function fetchJsonWithTimeout(url: string, timeoutMs: number): Promise<LoaderManifest> {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const signal = controller ? controller.signal : undefined;

  const request = fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
    signal: signal,
  }).then(function (response) {
    if (!response.ok) {
      throw new Error('Manifest request failed with status ' + response.status);
    }
    return response.json() as Promise<LoaderManifest>;
  });

  const timedRequest = withTimeout(request, timeoutMs, 'Manifest request timed out');

  timedRequest.catch(function () {
    if (controller) {
      controller.abort();
    }
  });

  return timedRequest;
}

function redirectToTvBundle(): void {
  window.location.replace(TV_BUNDLE_URL);
}

function loadFromCache(cached: LoaderState | null): void {
  if (!cached || !cached.manifest || !cached.manifest.scripts || cached.manifest.scripts.length === 0) {
    updateStatus('Loading...');
    clearState();
    redirectToTvBundle();

    return;
  }

  updateStatus('Loading ShareView...');

  bootRemote({
    version: cached.version,
    scripts: cached.manifest.scripts,
    styles: cached.manifest.styles,
  })
    .then(function () {
      updateStatus('');
    })
    .catch(function (error) {
      console.warn('Cached manifest failed to load', error);

      clearState();
      updateStatus('Failed to load update!');
      redirectToTvBundle();
    });
}

function handleManifestSuccess(manifest: LoaderManifest, cached: LoaderState | null): void {
  console.log('Received manifest: ', manifest);

  if (!manifest || !manifest.version) {
    console.warn('Manifest missing required fields', manifest);

    loadFromCache(cached);
    return;
  }

  if (!manifest.scripts || manifest.scripts.length === 0) {
    console.warn('Manifest scripts missing or empty', manifest);

    loadFromCache(cached);
    return;
  }

  if (manifest.minLoaderVersion && compareVersions(LOADER_VERSION, manifest.minLoaderVersion) < 0) {
    console.warn('Loader version is below required minimum', manifest.minLoaderVersion);

    updateStatus('Update required. Please contact support.');
    redirectToTvBundle();
    return;
  }

  const usingCached = cached && cached.version === manifest.version;

  updateStatus(usingCached ? 'Launching ShareView...' : 'Preparing ShareView...');

  bootRemote(manifest)
    .then(function () {
      storeState({
        version: manifest.version,
        manifest: {
          scripts: manifest.scripts,
          styles: manifest.styles,
        },
        lastOk: Date.now(),
      });
      updateStatus('ShareView is now ready.');
    })
    .catch(function (error) {
      console.warn('Unable to load manifest assets', error);

      if (cached && cached.manifest && cached.version !== manifest.version) {
        updateStatus('Update failed.');

        bootRemote({
          version: cached.version,
          scripts: cached.manifest.scripts,
          styles: cached.manifest.styles,
        })
          .then(function () {
            updateStatus('ShareView is now ready.');
          })
          .catch(function (fallbackError) {
            console.warn('Cached assets failed to load', fallbackError);

            clearState();
            updateStatus('Loading ShareView...');
            redirectToTvBundle();
          });
        return;
      }

      updateStatus('Loading ShareView...');
      redirectToTvBundle();
    });
}

function resolveApp(): void {
  if (__SVH_DISABLE_OTA__) {
    console.log('OTA disabled for this build. Launching bundled app.');
    clearState();
    updateStatus('Launching local bundle...');
    redirectToTvBundle();
    return;
  }

  updateStatus('Checking for updates...');

  const cached = getCachedState();

  fetchJsonWithTimeout(withCacheBust(CDN_MANIFEST_URL), MANIFEST_TIMEOUT_MS)
    .then(function (manifest) {
      handleManifestSuccess(manifest, cached);
    })
    .catch(function (error) {
      console.warn('Unable to fetch loader manifest', error);

      loadFromCache(cached);
    });
}

export function bootstrapLoader(): void {
  try {
    ensureRootElement();
  } catch (error) {
    console.warn('Root element missing; redirecting to TV bundle.', error);
    redirectToTvBundle();
    return;
  }

  resolveApp();
}
